import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getAllBriefData } from "../brief/thunks";
import { CompetitorType } from "../brief/types";
import { calculateSuggestedData } from "./helpers";
import {
  generateAutomatedInstructions,
  generateAutomatedInstructionsForOptimize,
  generateProjectLevelInstructions,
  getDescriptionSuggestions,
  getHeadingsSuggestions,
  getTitleSuggestions,
  scrapeArticleForOptimize,
  statisticsRequested
} from "./thunks";
import {
  AutomatedOutlineResponseType,
  BriefSuggestionData,
  Clusters,
  SuggestionType,
} from "./types";

const initialState: any = {
  isGeneratingInstructionsForOptimize: false,
  isScrapingForOptimize: false,
  isGeneratingProjectInstructions: false,
};

const initialSuggestionState: BriefSuggestionData = {
  titles: [],
  descriptions: [],
  topicsToCover: [],
  statistics: [],
  citedLinks: [],
  wordCount: 0,
  headings: 0,
  images: 0,
  contentGrade: "A",
  suggestedHeadings: [],
  loading: {},
  isGeneratingInstructions: false,
};

// Helper function to safely get brief data
const getBriefData = (state: SuggestionType, briefId: string): BriefSuggestionData => {
  const existing = state[briefId];
  return typeof existing === 'object' && existing ? existing : initialSuggestionState;
};

const suggestionsSlice = createSlice({
  name: "suggestions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        getAllBriefData.fulfilled,
        (
          state,
          action: PayloadAction<{
            data: {
              clusters: Clusters;
              competitors: CompetitorType[];
              questions: {
                title: string;
                vol?: number;
                id: string;
                selected: boolean;
              }[];
            };
          }> & { meta: { arg: string } }
        ) => {
          const briefId = action.meta.arg;

          const { wordCount, headings, images } = calculateSuggestedData(
            action.payload.data.competitors
          );
          return {
            ...state,
            [briefId]: {
              ...initialSuggestionState,
              wordCount,
              headings,
              images,
              clusters: action.payload.data.clusters,
            },
          };
        }
      )
      .addCase(
        getDescriptionSuggestions.pending,
        (
          state,
          action: PayloadAction<
            CompetitorType[],
            string,
            { arg: { keyword: string; title: string; briefId: string } }
          >
        ) => {
          const existingData = state[action.meta.arg.briefId];
          const briefData = typeof existingData === 'object' && existingData ? existingData : initialSuggestionState;
          return {
            ...state,
            [action.meta.arg.briefId]: {
              ...initialSuggestionState,
              ...briefData,
              loading: {
                ...briefData.loading,
                [action.type.replace("/pending", "")]: { status: "pending" },
              },
            },
          };
        }
      )
      .addCase(
        getDescriptionSuggestions.fulfilled,
        (
          state,
          action: PayloadAction<
            string[],
            string,
            { arg: { keyword: string; title: string; briefId: string } }
          >
        ) => {
          const briefData = state[action.meta.arg.briefId];
          if (typeof briefData === 'object' && briefData) {
            briefData.loading[action.type.replace("/fulfilled", "")] = { status: "finished" };
            briefData.descriptions = action.payload;
          }
        }
      )
      .addCase(
        statisticsRequested.pending,
        (state, action: PayloadAction<CompetitorType[]>) => {
          state.loading[action.type.replace("/pending", "")] = {
            status: "pending",
          };
        }
      )
      .addCase(
        getHeadingsSuggestions.pending,
        (
          state,
          action: PayloadAction<
            CompetitorType[],
            string,
            { arg: { startAt: string; briefId: string } }
          >
        ) => {
          const briefData = getBriefData(state, action.meta.arg.briefId);
          state[action.meta.arg.briefId] = {
            ...briefData,
            loading: {
              ...briefData.loading,
              [action.type.replace("/pending", "")]: {
                status: "pending",
                startAt: action.meta.arg.startAt,
              }
            }
          };
        }
      )
      .addCase(
        getTitleSuggestions.pending,
        (
          state,
          action: PayloadAction<
            CompetitorType[],
            string,
            {
              arg: {
                keyword: string;
                competitors: string[] | number[];
                briefId: string;
              };
            }
          >
        ) => {
          const existingData = state[action.meta.arg.briefId];
          const briefData = typeof existingData === 'object' && existingData ? existingData : initialSuggestionState;
          return {
            ...state,
            [action.meta.arg.briefId]: {
              ...initialSuggestionState,
              ...briefData,
              loading: {
                ...briefData.loading,
                [action.type.replace("/pending", "")]: { status: "pending" },
              },
            },
          };
        }
      )
      .addCase(
        getTitleSuggestions.fulfilled,
        (
          state,
          action: PayloadAction<
            string[],
            string,
            {
              arg: {
                keyword: string;
                competitors: string[] | number[];
                briefId: string;
              };
            }
          >
        ) => {
          state[action.meta.arg.briefId].loading[
            action.type.replace("/fulfilled", "")
          ] = { status: "finished" };
          state[action.meta.arg.briefId].titles = action.payload;
        }
      )
      .addCase(
        statisticsRequested.fulfilled,
        (
          state,
          action: PayloadAction<{ id: string; text: string; url: string }[]>
        ) => {
          state.loading[action.type.replace("/fulfilled", "")] = "finished";
        }
      )
      .addCase(
        getHeadingsSuggestions.fulfilled,
        (
          state,
          action: PayloadAction<
            AutomatedOutlineResponseType,
            string,
            {
              arg: {
                keyword: string;
                briefId: string;
              };
            }
          >
        ) => {
          if (!state[action.meta.arg.briefId]) {
            state[action.meta.arg.briefId] = {
              ...initialSuggestionState,
              loading: {
                "suggestions/getHeadingsSuggestions": {
                  status: "finished",
                  startAt: "",
                },
              },
            };
          }

          state[action.meta.arg.briefId].loading[
            "suggestions/getHeadingsSuggestions"
          ] = { status: "finished" };

          const similarityArray = Object.entries(
            action.payload.similarity_mapping
          );
          const suggestedHeadings = action.payload.parsed_outline.map(
            (parsedOutline) => {
              const similarRecord = similarityArray.find(
                ([key]) => key === parsedOutline.text
              );

              return {
                text: parsedOutline.text,
                tag: parsedOutline.tag,
                indent: Number(parsedOutline.tag.slice(1)),
                urls: similarRecord?.[1] ? Object.values(similarRecord[1]) : [],
                similarityMapping: similarRecord?.[1] ? similarRecord[1] : {},
              };
            }
          );
          state[action.meta.arg.briefId].suggestedHeadings = suggestedHeadings;
        }
      )
      .addCase(generateAutomatedInstructions.pending, (state, action) => {
        if (!state[action.meta.arg.briefId]) {
          state[action.meta.arg.briefId] = {
            ...initialSuggestionState,
            isGeneratingInstructions: true,
          };
        } else {
          state[action.meta.arg.briefId].isGeneratingInstructions = true;
        }
      })
      .addCase(generateAutomatedInstructions.fulfilled, (state, action) => {
        if (state[action.meta.arg.briefId]) {
          state[action.meta.arg.briefId].isGeneratingInstructions = false;
        }
      })
      .addCase(generateAutomatedInstructions.rejected, (state, action) => {
        if (state[action.meta.arg.briefId]) {
          state[action.meta.arg.briefId].isGeneratingInstructions = false;
        }
      })
      .addCase(generateProjectLevelInstructions.pending, (state) => {
        state.isGeneratingProjectInstructions = true;
      })
      .addCase(generateProjectLevelInstructions.fulfilled, (state) => {
        state.isGeneratingProjectInstructions = false;
      })
      .addCase(generateProjectLevelInstructions.rejected, (state) => {
        state.isGeneratingProjectInstructions = false;
      })
      .addCase(generateAutomatedInstructionsForOptimize.pending, (state) => {
        state.isGeneratingInstructionsForOptimize = true;
      })
      .addCase(generateAutomatedInstructionsForOptimize.fulfilled, (state) => {
        state.isGeneratingInstructionsForOptimize = false;
      })
      .addCase(generateAutomatedInstructionsForOptimize.rejected, (state) => {
        state.isGeneratingInstructionsForOptimize = false;
      })
      .addCase(scrapeArticleForOptimize.pending, (state) => {
        state.isScrapingForOptimize = true;
      })
      .addCase(scrapeArticleForOptimize.fulfilled, (state) => {
        state.isScrapingForOptimize = false;
      })
      .addCase(scrapeArticleForOptimize.rejected, (state) => {
        state.isScrapingForOptimize = false;
      });
  },
});
export * from "./selectors";
export * from "./thunks";

export default suggestionsSlice.reducer;
